"use client";

import { ArrowRight, Check, Flag, Heart, MapPin, UserRound, Users, WalletCards, Search, Bell } from "lucide-react";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const TEELISTCLUB_LOGO_URL = "https://ideal-jay.10web.cloud/wp-content/uploads/2026/08/ChatGPT-Image-Aug-6-2026-12_43_40-AM-e1785948351998.png";
const featurePoints = ["Playing level & preferences", "Travel style & companions", "Budget & trip details", "Favourite destinations & more"];
const passportRows = [{
  icon: UserRound,
  label: "Playing Level",
  value: "Intermediate"
}, {
  icon: Users,
  label: "Travel Style",
  value: "Couple"
}, {
  icon: WalletCards,
  label: "Budget",
  value: "$$$"
}, {
  icon: Flag,
  label: "Preferred Courses",
  value: "Resort, Scenic"
}, {
  icon: MapPin,
  label: "Favourite Destinations",
  value: "Thailand, Vietnam"
}];
function TeeListClubPhoneLogo() {
  return <div className="flex min-h-[42px] items-center justify-center">
      <img src={TEELISTCLUB_LOGO_URL} alt="TeeListClub — Premium Golf Discovery for Asia" loading="eager" decoding="async" className="h-[38px] w-auto max-w-[132px] object-contain" />
    </div>;
}
function GolfPinGraphic() {
  return <svg viewBox="0 0 140 120" className="h-[96px] w-[96px]" fill="none" aria-hidden="true">
      <ellipse cx="74" cy="90" rx="26" ry="7" fill="#F0E8D5" stroke="#B88F46" strokeWidth="2" />

      <path d="M48 90V34" stroke="#0B5A45" strokeWidth="2.2" strokeLinecap="round" />

      <path d="M48 34L84 44L48 58V34Z" fill="#BFFF00" stroke="#98C900" strokeWidth="1.3" strokeLinejoin="round" />

      <path d="M50 89C55 86.5 63 85 73 85C83 85 91 86.5 96 89" stroke="#B88F46" strokeWidth="2" strokeLinecap="round" />
    </svg>;
}
export default function Golfpassportintro() {
  const {
    ref: leftRef,
    isVisible: leftVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const {
    ref: rightRef,
    isVisible: rightVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.12
  });
  return <section data-section-id="3135" id="golfpassportintro" aria-labelledby="golfpassportintro-title" className="relative overflow-visible bg-[#F7F3E9] py-[60px] text-[#073F31] lg:min-h-[800px] lg:overflow-hidden">
      {/* Subtle paper texture */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 opacity-[0.18]" style={{
      backgroundImage: "radial-gradient(circle at 20% 20%, rgba(184,143,70,0.05) 0, rgba(184,143,70,0.05) 1px, transparent 1px), radial-gradient(circle at 80% 30%, rgba(7,63,49,0.03) 0, rgba(7,63,49,0.03) 1px, transparent 1px)",
      backgroundSize: "24px 24px, 30px 30px"
    }} />

      {/* Contour lines on right */}
      <div aria-hidden="true" className="pointer-events-none absolute bottom-[-40px] right-[-40px] hidden h-[520px] w-[620px] opacity-55 lg:block">
        <svg viewBox="0 0 620 520" fill="none" className="h-full w-full">
          <path d="M54 367C122 311 183 325 236 267C289 209 275 148 351 119C430 89 489 129 566 120C592 117 607 109 620 104" stroke="#D9BE88" strokeWidth="1.35" />

          <path d="M36 404C114 335 181 355 248 293C312 234 300 172 383 145C461 120 521 154 620 139" stroke="#D9BE88" strokeWidth="1.35" />

          <path d="M20 442C98 366 183 391 271 318C346 256 343 198 420 173C493 149 553 188 620 176" stroke="#D9BE88" strokeWidth="1.35" />

          <path d="M8 481C90 394 187 426 296 352C385 291 391 227 467 203C530 183 582 219 620 214" stroke="#D9BE88" strokeWidth="1.35" />
        </svg>
      </div>

      <div className="relative mx-auto grid w-full max-w-[1480px] grid-cols-1 items-center gap-12 px-5 sm:px-8 md:px-12 lg:grid-cols-[48%_52%] lg:gap-16 lg:px-16">
        {/* Left copy */}
        <div ref={leftRef} className={`relative z-10 transition-all duration-1000 ease-out ${leftVisible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"}`}>
          <p className="mb-5 text-sm font-bold uppercase tracking-[0.24em] text-[#B88F46]" style={{
          fontFamily: "'Raleway', Arial, sans-serif"
        }}>
            <span className="font-bold">Your Profile. Your Journey</span>.
          </p>

          <h2 id="golfpassportintro-title" className="max-w-[650px] font-normal leading-[1.05] tracking-[-0.04em] text-[#073F31]" style={{
          fontFamily: "'Playfair Display', Georgia, serif",
          fontSize: "clamp(3rem, 4.5vw, 5rem)"
        }}>
            Your golf preferences,
            <br />
            all in{" "}
            <em className="font-normal text-[#0B5A45]">one place.</em>
          </h2>

          <div aria-hidden="true" className="mt-7 h-px w-12 bg-[#B88F46]" />

          <p className="mt-7 max-w-[585px] text-[15px] font-normal leading-[1.85] text-[#4F6961] sm:text-[16px]" style={{
          fontFamily: "'Raleway', Arial, sans-serif"
        }}>
            Set your playing level, travel style, preferred course types and
            budget. The more you tell us, the more relevant your course
            discoveries and travel ideas can become.
          </p>

          <ul className="mt-8 space-y-4" style={{
          fontFamily: "'Raleway', Arial, sans-serif"
        }}>
            {featurePoints.map(item => <li key={item} className="flex items-center gap-3 text-[14px] font-medium text-[#355B50] sm:text-[15px]">
                <span className="flex size-5 shrink-0 items-center justify-center rounded-full bg-[#E8F4D4] text-[#0B5A45]">
                  <Check aria-hidden="true" className="size-3.5" strokeWidth={2} />
                </span>

                <span>{item}</span>
              </li>)}
          </ul>

          <div className="mt-10">
           <button type="button" disabled aria-disabled="true" className="inline-flex min-h-[52px] cursor-not-allowed items-center justify-center gap-3 rounded-md bg-[#073F31] px-7 py-3 text-[12px] font-bold uppercase tracking-[0.09em] text-[#FFFDF7] opacity-75 shadow-[0_12px_30px_rgba(7,63,49,0.16)]" style={{
            fontFamily: "'Raleway', Arial, sans-serif"
          }}>
  Golf Passport — Coming Soon
</button>
          </div>
        </div>

        {/* Right visual */}
        <div ref={rightRef} className={`relative flex min-h-[600px] items-center justify-center transition-all duration-1000 delay-150 ease-out sm:min-h-[640px] lg:min-h-[700px] ${rightVisible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"}`}>
          {/* Soft halo */}
          <div aria-hidden="true" className="absolute left-1/2 top-1/2 h-[300px] w-[300px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-[#EEE3CC]/70 blur-3xl sm:h-[420px] sm:w-[420px]" />

          {/* Pin */}
          <div aria-hidden="true" className="absolute bottom-[12%] right-[8%] hidden lg:block">
            <GolfPinGraphic />
          </div>

          {/* Phone */}
          <div className="relative z-10 w-[280px] max-w-[286px] rounded-[42px] border-[7px] border-[#121619] bg-[#121619] p-[3px] shadow-[0_24px_58px_rgba(26,39,34,0.24)] sm:max-w-[300px]">
            {/* Side buttons */}
            <div className="absolute -left-[4px] top-[120px] h-[38px] w-[3px] rounded-full bg-[#9AA1A4]" />
            <div className="absolute -left-[4px] top-[172px] h-[46px] w-[3px] rounded-full bg-[#9AA1A4]" />
            <div className="absolute -right-[4px] top-[150px] h-[60px] w-[3px] rounded-full bg-[#9AA1A4]" />

            <div className="relative overflow-hidden rounded-[34px] bg-[#FFFDF7]">
              {/* Top status/app area */}
              <div className="relative bg-[linear-gradient(90deg,#044C3D_0%,#095D43_55%,#0A4B3F_100%)] px-4 pb-4 pt-4 text-[#FFFDF7]">
                <div className="mb-3 flex items-center justify-between text-[10px] font-semibold text-white">
                  <span>9:36</span>

                  <div className="absolute left-1/2 top-[10px] h-[16px] w-[74px] -translate-x-1/2 rounded-full bg-[#13181B]" />

                  <div className="flex items-center gap-1.5">
                    <span className="h-[6px] w-[6px] rounded-full bg-white/90" />
                    <span className="h-[6px] w-[10px] rounded-[2px] border border-white/90" />
                  </div>
                </div>

                <div className="mb-3 flex items-center justify-between">
                  <button type="button" aria-label="Search" className="flex size-7 items-center justify-center rounded-full bg-white/8 text-white/90">
                    <Search className="size-3.5" strokeWidth={1.8} />
                  </button>

                  <TeeListClubPhoneLogo />

                  <button type="button" aria-label="Notifications" className="flex size-7 items-center justify-center rounded-full bg-white/8 text-white/90">
                    <Bell className="size-3.5" strokeWidth={1.8} />
                  </button>
                </div>
              </div>

              {/* Content */}
              <div className="bg-[#FFFDF7] px-4 pb-3 pt-4">
                <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-[#073F31]" style={{
                fontFamily: "'Raleway', Arial, sans-serif"
              }}>
                  Golf Passport
                </p>

                <p className="mt-1 text-[11px] font-medium text-[#72827C]" style={{
                fontFamily: "'Raleway', Arial, sans-serif"
              }}>
                  Your Golf Profile
                </p>

                <div className="mt-3 flex items-center justify-between text-[9px] font-medium text-[#7D8B86]">
                  <span>60% Complete</span>
                  <span>100% Complete</span>
                </div>

                <div className="mt-2 h-[5px] overflow-hidden rounded-full bg-[#E3E4DE]">
                  <div className="h-full w-[58%] rounded-full bg-[#BFFF00]" />
                </div>
              </div>

              {/* Rows */}
              <div className="bg-[#FFFDF7] px-3 pb-2">
                {passportRows.map((row, index) => {
                const Icon = row.icon;
                return <div key={row.label} className={`flex items-center gap-3 px-2 py-3 ${index !== passportRows.length - 1 ? "border-b border-[#E9E3D8]" : ""}`}>
                      <div className="flex size-9 shrink-0 items-center justify-center rounded-full bg-[#EFF2EA] text-[#0B5A45]">
                        <Icon className="size-4" strokeWidth={1.7} />
                      </div>

                      <div className="min-w-0 flex-1">
                        <p className="text-[9px] font-medium text-[#72827C]" style={{
                      fontFamily: "'Raleway', Arial, sans-serif"
                    }}>
                          {row.label}
                        </p>

                        <p className="mt-0.5 truncate text-[10px] font-semibold text-[#073F31]" style={{
                      fontFamily: "'Raleway', Arial, sans-serif"
                    }}>
                          {row.value}
                        </p>
                      </div>

                      <ArrowRight className="size-4 shrink-0 text-[#A0AAA5]" strokeWidth={1.6} />
                    </div>;
              })}
              </div>

              {/* Bottom navigation */}
              <div className="grid grid-cols-4 border-t border-[#E7E1D6] bg-[#FFFDF9] px-2 py-3">
                <div className="flex flex-col items-center gap-1 text-[#9AA39E]">
                  <Flag className="size-3.5" strokeWidth={1.7} />

                  <span className="text-[7px] font-medium uppercase tracking-[0.05em]">
                    Discover
                  </span>
                </div>

                <div className="flex flex-col items-center gap-1 text-[#9AA39E]">
                  <MapPin className="size-3.5" strokeWidth={1.7} />

                  <span className="text-[7px] font-medium uppercase tracking-[0.05em]">
                    Lists
                  </span>
                </div>

                <div className="flex flex-col items-center gap-1 text-[#0B5A45]">
                  <Heart className="size-3.5" strokeWidth={1.9} />

                  <span className="text-[7px] font-bold uppercase tracking-[0.05em]">
                    Passport
                  </span>
                </div>

                <div className="flex flex-col items-center gap-1 text-[#9AA39E]">
                  <UserRound className="size-3.5" strokeWidth={1.7} />

                  <span className="text-[7px] font-medium uppercase tracking-[0.05em]">
                    Profile
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}